import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { SERVICES, SHORT_NAMES, API } from "@/data/site";

const inputCls =
  "w-full border border-[#262A35] bg-[#0B0C0E] px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:border-[#3B82F6] focus:outline-none transition-colors";

function Stars({ value, onChange, size = "h-4 w-4", testidPrefix }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={onChange ? `${testidPrefix}-${n}` : undefined}
          onClick={onChange ? () => onChange(n) : undefined}
          className={onChange ? "cursor-pointer" : "cursor-default"}
          aria-label={`${n} star`}
        >
          <Star className={`${size} ${n <= value ? "fill-[#3B82F6] text-[#3B82F6]" : "text-[#3A3F50]"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", vehicle: "", service: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch {
      toast.error("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? reviews : reviews.filter((r) => r.service === filter)),
    [reviews, filter]
  );

  const average = useMemo(
    () => (reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "—"),
    [reviews]
  );

  const serviceName = (id) => SERVICES.find((s) => s.id === id)?.title || id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.vehicle || !form.service || !form.comment) {
      toast.error("Please complete all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thanks! Your review is now live.");
      setForm({ name: "", vehicle: "", service: "", rating: 5, comment: "" });
      fetchReviews();
    } catch {
      toast.error("Could not submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="reviews-page" className="pt-16">
      <Seo
        title="Customer Reviews — Bowegans Car Care, Palani"
        siteName="Bowegans Car Care"
        description="Verified customer reviews for Bowegans Car Care, Palani — PPF, audio, alloy wheels, seat covers and more. Rated 4.9/5 by car owners."
      />

      <section className="border-b border-white/10 bg-[#0E1013]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <SectionHeading
            testid="reviews-page-heading"
            eyebrow="Customer reviews"
            title="Word from the driver's seat."
            description="Real feedback from real fittings. Every review below is from a vehicle that rolled out of our Palani bay."
          />
          <Reveal delay={0.2} className="mt-8">
            <div
              className="inline-flex flex-wrap items-center gap-6 border border-[#262A35] bg-[#121418] px-6 py-5"
              data-testid="reviews-summary-banner"
            >
              <span className="font-display text-5xl font-black text-white">{average}</span>
              <div>
                <Stars value={Math.round(Number(average)) || 5} size="h-5 w-5" />
                <p className="font-mono2 mt-1.5 text-[10px] uppercase tracking-[0.25em] text-gray-500">
                  Based on {reviews.length}+ verified fittings
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="flex flex-wrap gap-2" data-testid="reviews-filter-bar">
          <button
            data-testid="reviews-filter-all"
            onClick={() => setFilter("all")}
            className={`font-mono2 border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-colors ${
              filter === "all"
                ? "border-[#3B82F6] bg-[#3B82F6] text-white"
                : "border-[#262A35] text-gray-400 hover:border-[#3B82F6] hover:text-white"
            }`}
          >
            All Services
          </button>
          {SERVICES.map((s) => (
            <button
              key={s.id}
              data-testid={`reviews-filter-${s.id}`}
              onClick={() => setFilter(s.id)}
              className={`font-mono2 border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-colors ${
                filter === s.id
                  ? "border-[#3B82F6] bg-[#3B82F6] text-white"
                  : "border-[#262A35] text-gray-400 hover:border-[#3B82F6] hover:text-white"
              }`}
            >
              {SHORT_NAMES[s.id] || s.title}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="font-mono2 mt-12 text-xs uppercase tracking-[0.3em] text-gray-500" data-testid="reviews-loading">
            Loading reviews...
          </p>
        ) : filtered.length === 0 ? (
          <p className="font-mono2 mt-12 text-xs uppercase tracking-[0.3em] text-gray-500" data-testid="reviews-empty">
            No reviews for this service yet — be the first below.
          </p>
        ) : (
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="reviews-grid">
            {filtered.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.08} className="h-full">
                <div className="spotlight-card flex h-full flex-col border border-[#262A35] bg-[#121418] p-6" data-testid={`review-card-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <Stars value={r.rating} />
                    <Quote className="h-5 w-5 text-[#3A3F50]" />
                  </div>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-gray-300">"{r.comment}"</p>
                  <div className="mt-5 border-t border-white/10 pt-4">
                    <p className="font-display text-sm font-bold text-white">{r.name}</p>
                    <p className="font-mono2 mt-1 text-[10px] uppercase tracking-[0.2em] text-gray-500">
                      {r.vehicle} · {serviceName(r.service)}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-white/10 bg-[#0E1013]" data-testid="review-submit-section">
        <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
          <SectionHeading
            testid="review-submit-heading"
            eyebrow="Share your experience"
            title="Got the Bowegans finish?"
          />
          <Reveal delay={0.15} className="mt-10">
            <form onSubmit={submit} className="space-y-4 border border-[#262A35] bg-[#121418] p-6 sm:p-8" data-testid="review-submit-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="review-submit-name-input"
                  className={inputCls}
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  data-testid="review-submit-vehicle-input"
                  className={inputCls}
                  placeholder="Vehicle (e.g. Swift 2022) *"
                  value={form.vehicle}
                  onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
                />
              </div>
              <select
                data-testid="review-submit-service-select"
                className={inputCls}
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
              >
                <option value="">Service received *</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
              <div>
                <p className="font-mono2 mb-2 text-[10px] uppercase tracking-[0.25em] text-gray-500">Your rating *</p>
                <Stars
                  value={form.rating}
                  onChange={(n) => setForm({ ...form, rating: n })}
                  size="h-6 w-6"
                  testidPrefix="review-submit-rating"
                />
              </div>
              <textarea
                data-testid="review-submit-comment-input"
                rows={4}
                className={inputCls}
                placeholder="How was the fitting, finish and service? *"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="review-submit-button"
                className="font-mono2 w-full bg-[#3B82F6] px-6 py-4 text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#2563EB] disabled:opacity-60"
              >
                {submitting ? "Posting..." : "Post Review"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
